import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { storageBoxItemFromTrack } from './audioPlaylistStorage';

const GlobalStorageBoxAudioContext = createContext(null);

function useDocumentThemeMode() {
  const [mode, setMode] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
      ? 'dark'
      : 'light'
  );

  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const observer = new MutationObserver(() => {
      setMode(document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return mode;
}

export function GlobalStorageBoxAudioProvider({ children }) {
  const theme = useDocumentThemeMode();
  const [fileItem, setFileItem] = useState(null);
  const [initialPlaylist, setInitialPlaylist] = useState(null);
  const [publicToken, setPublicToken] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [inlineHostEl, setInlineHostEl] = useState(null);
  const sessionApiRef = useRef(null);

  const isActive = Boolean(fileItem?.itemId);

  const registerInlineHost = useCallback((el) => {
    setInlineHostEl(el || null);
  }, []);

  const openFile = useCallback((item, options = {}) => {
    if (!item?.itemId) return;
    const { initialPlaylist: bootstrap = null, publicToken: token = null, resetPlaylist = false } =
      options;

    if (token != null) {
      setPublicToken(token);
    }

    if (bootstrap) {
      setInitialPlaylist(bootstrap);
      setFileItem(item);
      setMinimized(false);
      return;
    }

    if (resetPlaylist) {
      setInitialPlaylist(null);
      setFileItem(item);
      setMinimized(false);
      return;
    }

    setFileItem((prev) => {
      if (prev?.itemId === item.itemId) {
        return prev;
      }
      setMinimized(false);
      return item;
    });
  }, []);

  const playFileNow = useCallback(
    (item, options = {}) => {
      if (!item?.itemId) return;
      if (fileItem?.itemId && sessionApiRef.current?.playItemNow) {
        sessionApiRef.current.playItemNow(item);
        return;
      }
      openFile(item, { ...options, resetPlaylist: !options.initialPlaylist });
    },
    [fileItem?.itemId, openFile]
  );

  const loadStorageBoxPlaylist = useCallback((row, storageTracks) => {
    const firstItem = storageBoxItemFromTrack(storageTracks[0]);
    if (!firstItem?.itemId) return;
    openFile(firstItem, {
      initialPlaylist: {
        id: row.id,
        tracks: storageTracks,
        meta: {
          id: row.id,
          name: row.name,
          shuffle: row.shuffle,
          repeat: row.repeat,
          eqGains: row.eqGains,
        },
      },
    });
  }, [openFile]);

  const queueFileNext = useCallback(
    (item) => {
      if (!item?.itemId) return;
      if (fileItem?.itemId && sessionApiRef.current?.queuePlayNext) {
        sessionApiRef.current.queuePlayNext(item);
        return;
      }
      playFileNow(item, { resetPlaylist: true });
    },
    [fileItem?.itemId, playFileNow]
  );

  const closeSession = useCallback(() => {
    const api = sessionApiRef.current;
    if (api?.playing) {
      api.togglePlay?.();
    }
    setFileItem(null);
    setInitialPlaylist(null);
    setPublicToken(null);
    setMinimized(false);
  }, []);

  const value = useMemo(
    () => ({
      theme,
      isActive,
      fileItem,
      initialPlaylist,
      publicToken,
      minimized,
      setMinimized,
      inlineHostEl,
      registerInlineHost,
      sessionApiRef,
      openFile,
      playFileNow,
      loadStorageBoxPlaylist,
      queueFileNext,
      closeSession,
    }),
    [
      theme,
      isActive,
      fileItem,
      initialPlaylist,
      publicToken,
      minimized,
      inlineHostEl,
      registerInlineHost,
      openFile,
      playFileNow,
      loadStorageBoxPlaylist,
      queueFileNext,
      closeSession,
    ]
  );

  return (
    <GlobalStorageBoxAudioContext.Provider value={value}>{children}</GlobalStorageBoxAudioContext.Provider>
  );
}

export function useGlobalStorageBoxAudio() {
  const ctx = useContext(GlobalStorageBoxAudioContext);
  if (!ctx) {
    throw new Error('useGlobalStorageBoxAudio must be used within GlobalStorageBoxAudioProvider');
  }
  return ctx;
}

export function useOptionalGlobalStorageBoxAudio() {
  return useContext(GlobalStorageBoxAudioContext);
}
